import { useEffect, useState } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Two states, because removal means two different things (task #437):
//
//   routine   — they're still in another group you coach, so you keep seeing
//               them. Quiet confirm.
//   last group — this is the last group you share, so removing ends your
//               ability to see or re-add them. Warn, and offer to move them
//               instead.
//
// The old copy said "Their account is preserved — you can add them back later"
// unconditionally. That was false in the second case and is what produced the
// client report: the coach was promised a re-add that the Add Member pool
// could not offer. The sentence is gone from BOTH states rather than made
// conditional — it was only ever reassuring, never informative.
//
// `member.id` is the group_membership id to delete.
export function RemoveMemberConfirm({
  member,
  groupName,
  coachedGroups = [],
  onCancel,
  onConfirm,
  onTransfer,
}) {
  const [targetGroupId, setTargetGroupId] = useState("")

  // Whether removal costs this coach their visibility is decided SERVER-side
  // (`losesCoachVisibility`) and simply read here. Deriving it from
  // `coachedGroups` looked equivalent and wasn't: rosters expose only the
  // shared-group path of Coach visibility, never `assigned_coach_id`, so a
  // client-side derivation warns falsely about a coach's own converted
  // prospect — the very people task #437 just made reachable.
  const losesVisibility = !!member && member.losesCoachVisibility

  // The destination list is a different question — "which of my groups are
  // they NOT in" — and that IS answerable from the rosters already loaded.
  const otherGroups = coachedGroups.filter(
    (g) =>
      g.id !== member?.groupId &&
      !g.members?.some((m) => m.accountId === member?.accountId)
  )
  const canTransfer = losesVisibility && otherGroups.length > 0

  // Reset the picker each time the dialog opens on a different person, so a
  // stale destination can never be submitted.
  useEffect(() => {
    setTargetGroupId("")
  }, [member?.id])

  if (!member) return null

  return (
    <AlertDialog open={!!member} onOpenChange={(o) => { if (!o) onCancel() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {losesVisibility ? `Remove ${member.name} from your groups?` : `Remove ${member.name}?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This removes <span className="font-medium text-zinc-700">{member.name}</span> from{" "}
            <span className="font-medium text-zinc-700">{groupName}</span>.
            {losesVisibility ? (
              <>
                {" "}
                <span className="font-medium text-red-700">
                  {groupName} is the only group you share with them, so you&apos;ll no longer
                  be able to see them or add them back.
                </span>{" "}
                Their account and their history are kept — an admin can still reach them.
              </>
            ) : (
              " They stay in your other groups, so you'll still see them."
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {canTransfer && (
          <div className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-3">
            <p className="text-sm font-medium text-zinc-700">
              Move them to another group instead
            </p>
            <Select value={targetGroupId} onValueChange={setTargetGroupId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {otherGroups.map((g) => (
                  <SelectItem key={g.id} value={String(g.id)}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="w-full"
              disabled={!targetGroupId}
              onClick={() => onTransfer(member.id, targetGroupId)}
            >
              Move to group
            </Button>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => onConfirm(member.id)}
          >
            {canTransfer ? "Remove anyway" : "Remove"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
